import type { ReactElement } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Download, Loader2, Share, Sparkles } from 'lucide-react';
import { PortalDsBadge, PortalDsButton, portalDs } from 'src/components/portal-design-system';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import SurveyResponderTrigger from 'src/components/criteria/survey-responder-trigger';
import { apiUrl } from 'src/constants/config';
import useShareSurvey from 'src/hooks/useShareSurvey';
import { AnswerSource, SurveyFull, SurveyStatus, type SurveyStats } from 'src/redux/survey/survey.types';
import { getIsUserOrg, getUserStateActiveClient } from 'src/redux/user/user.selectors';
import ReduxService from 'src/redux/redux.service';
import { RootState } from 'src/redux/redux-store';
import InvisiblyApiService from 'src/services/api/invisibly-api.service';
import { hasSurveyAIPanelAccess } from 'src/services/permission/permission.helpers';
import { createShare } from 'src/services/surveys/surveys.helpers';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import BreakoutControls from './breakout-controls';
import SourceFilterControl from './source-filter-control';
import type { RespondentScope } from '../../services/demographic-breakout.helpers';
import type { SourceFilterOption } from '../../services/source-filter.helpers';

interface OverviewHeaderProps {
  survey?: SurveyFull;
  stats?: SurveyStats;
  isShare?: boolean;
  breakoutAttribute: string | null;
  respondentScope: RespondentScope;
  sourceFilter: AnswerSource;
  sourceFilterOptions: SourceFilterOption[];
  onBreakoutChange: (attributeKey: string | null) => void;
  onRespondentScopeChange: (scope: RespondentScope) => void;
  onSourceFilterChange: (source: AnswerSource) => void;
}

function csvDownloadUrl(surveyId: string, isShare: boolean): string {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  const baseUrl = isClientActive
    ? `${apiUrl}/b2b/survey/${surveyId}/csv`.replace('b2b', 'client')
    : `${apiUrl}/b2b/survey/${surveyId}/csv`;
  let url = createShare(baseUrl, isShare, 'b2b');
  if (isClientActive) {
    url = `${url}?client_id=${state.userState.activeClient?.ext_client_id}`;
  }
  return url;
}

function statusBadgeVariant(status?: SurveyStatus): PortalDsBadgeVariant {
  switch (status) {
    case SurveyStatus.Live:
      return 'blue';
    case SurveyStatus.Complete:
      return 'green';
    case SurveyStatus.Review:
    case SurveyStatus.Rejected:
    case SurveyStatus.Failed:
      return 'red';
    case SurveyStatus.Retry:
      return 'amber';
    default:
      return 'gray';
  }
}

function OverviewHeader({
  survey,
  stats,
  isShare = false,
  breakoutAttribute,
  respondentScope,
  sourceFilter,
  sourceFilterOptions,
  onBreakoutChange,
  onRespondentScopeChange,
  onSourceFilterChange,
}: OverviewHeaderProps): ReactElement {
  const client = useSelector(getUserStateActiveClient);
  const isOrgUser = useSelector(getIsUserOrg);
  const [isDownloading, setIsDownloading] = useState(false);
  const surveyId = survey?.extSurveyId ?? '';
  const { handleShare, canShare, isLoading: isSharing } = useShareSurvey({
    surveyId,
    surveyStatus: survey?.surveyStatus,
  });
  const canDownload = survey?.surveyStatus === SurveyStatus.Complete
    || survey?.surveyStatus === SurveyStatus.Live;
  const canShowAIPanel = Boolean(surveyId)
    && !isShare
    && (isOrgUser || hasSurveyAIPanelAccess({ permissions: client?.permissions }));

  const handleDownload = async() => {
    if (!survey || !canDownload) {
      return;
    }
    const fileName = `${survey.title.trim().toLowerCase().split(' ').join('_')}.csv`;
    setIsDownloading(true);
    try {
      await InvisiblyApiService.invokeDownloadRequest(csvDownloadUrl(survey.extSurveyId, isShare), fileName);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="border-b border-[#EDEEF2] bg-white px-6 py-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <h1 className={cn(portalDs.sectionTitle, 'truncate text-[22px] tracking-[-0.6px]')}>
                  {survey?.title ?? 'Survey results'}
                </h1>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-md"
              >
                {survey?.title}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {survey?.surveyStatus ? (
            <div className="mt-2">
              <PortalDsBadge
                variant={statusBadgeVariant(survey.surveyStatus)}
                pill
              >
                {survey.surveyStatus}
              </PortalDsBadge>
            </div>
          ) : null}
          <div className="mt-5 flex flex-col gap-3">
            <BreakoutControls
              stats={stats}
              breakoutAttribute={breakoutAttribute}
              respondentScope={respondentScope}
              onBreakoutChange={onBreakoutChange}
              onRespondentScopeChange={onRespondentScopeChange}
            />
            <SourceFilterControl
              sourceFilter={sourceFilter}
              options={sourceFilterOptions}
              onSourceFilterChange={onSourceFilterChange}
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <PortalDsButton
            size="sm"
            variant="secondary"
            aria-label="Download"
            title={canDownload ? 'Download survey data as a CSV' : 'Download is available when the survey is live or complete'}
            disabled={!canDownload || isDownloading}
            onClick={() => { void handleDownload(); }}
          >
            {isDownloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
            Download
          </PortalDsButton>
          <PortalDsButton
            size="sm"
            variant="secondary"
            aria-label="Share"
            title={canShare ? 'Copy a publicly shareable link to this survey’s results' : 'Share is available when the survey is live or complete'}
            disabled={!canShare || isSharing}
            onClick={handleShare}
          >
            {isSharing ? <Loader2 className="size-3.5 animate-spin" /> : <Share className="size-3.5" />}
            Share
          </PortalDsButton>
          {canShowAIPanel ? (
            <SurveyResponderTrigger
              extSurveyId={surveyId}
              trigger={(
                <PortalDsButton
                  size="sm"
                  variant="accent"
                  aria-label="AI panelists"
                  title="Add AI panelists"
                >
                  <Sparkles className="size-3.5" />
                  AI
                </PortalDsButton>
              )}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default OverviewHeader;
